import React from 'react';
import { MODULE_ICONS, NAV_ICONS } from '../navConfig';

// Dark theme only: a very dark brand colour (e.g. HR's navy) gives no glow on a dark card, so it is
// lifted towards white until it reads. The light theme keeps using the colour as it is.
const liftForDark = (hex) => {
  const m = /^#?([0-9a-f]{6})/i.exec(hex || '');
  if (!m) return hex;
  const [r, g, b] = [0, 2, 4].map(i => parseInt(m[1].slice(i, i + 2), 16));
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  if (lum >= 0.32) return `#${m[1]}`;
  const t = Math.min(0.6, (0.32 - lum) * 2.2);   // how far to move towards white
  const mix = (c) => Math.round(c + (255 - c) * t).toString(16).padStart(2, '0');
  return `#${mix(r)}${mix(g)}${mix(b)}`;
};

// `icon` and `color` override the module's own (used by hubs whose cards are not NAV modules, e.g. HR's submenus)
export default function ModuleCard({ item, onOpen, icon, color: colorOverride, badge }) {
  const card = MODULE_ICONS[item.id];
  const color = colorOverride || card?.color || '#2563eb';
  return (
    <>
      <style>{`
        .module-card:hover .module-card-btn {
          background: linear-gradient(135deg, #071e53 0%, #0e3b9e 100%) !important;
          box-shadow: 0 6px 18px rgba(3, 21, 64, 0.55) !important;
          transform: translateY(-1px);
        }
        .module-card:hover .module-card-arrow {
          transform: translateX(4px);
        }
        .module-card:active .module-card-btn {
          transform: translateY(1px);
        }

        /* ── Dark theme only ────────────────────────────────────────────────────
           The inline colours above are the light design; these override them in
           dark mode (light theme untouched). Each card takes its module colour
           from --mc: a soft glow at the top, a hairline border, a tinted icon
           tile and an "Open" pill that fills with the colour on hover. */
        [data-theme="dark"] .module-card {
          background:
            radial-gradient(120% 70% at 50% -10%, color-mix(in srgb, var(--mc) 16%, transparent) 0%, transparent 62%),
            linear-gradient(180deg, #13243a 0%, #0e1b2b 100%) !important;
          border: 1px solid rgba(148, 163, 184, 0.14) !important;
          box-shadow: 0 1px 0 rgba(255,255,255,0.04) inset, 0 10px 28px -14px rgba(0,0,0,0.7) !important;
        }
        [data-theme="dark"] .module-card:hover {
          border-color: color-mix(in srgb, var(--mc) 55%, transparent) !important;
          box-shadow:
            0 1px 0 rgba(255,255,255,0.05) inset,
            0 0 0 1px color-mix(in srgb, var(--mc) 22%, transparent),
            0 22px 44px -16px color-mix(in srgb, var(--mc) 45%, transparent) !important;
        }
        [data-theme="dark"] .module-card:focus-visible {
          outline: 2px solid color-mix(in srgb, var(--mc) 70%, white);
          outline-offset: 2px;
        }
        [data-theme="dark"] .module-card .module-card-icon {
          background: color-mix(in srgb, var(--mc) 20%, #0b1624) !important;
          border: 1px solid color-mix(in srgb, var(--mc) 40%, transparent) !important;
          color: color-mix(in srgb, var(--mc) 55%, #ffffff) !important;
          box-shadow: 0 6px 18px -8px color-mix(in srgb, var(--mc) 60%, transparent);
        }
        [data-theme="dark"] .module-card .module-card-title { color: #eef4fa !important; }
        [data-theme="dark"] .module-card .module-card-desc { color: #93a5b8 !important; }
        [data-theme="dark"] .module-card .module-card-btn {
          background: color-mix(in srgb, var(--mc) 16%, transparent) !important;
          border: 1px solid color-mix(in srgb, var(--mc) 45%, transparent) !important;
          color: color-mix(in srgb, var(--mc) 45%, #ffffff) !important;
          box-shadow: none !important;
        }
        [data-theme="dark"] .module-card:hover .module-card-btn {
          background: color-mix(in srgb, var(--mc) 80%, #0b1624) !important;
          border-color: color-mix(in srgb, var(--mc) 90%, white) !important;
          color: #ffffff !important;
          box-shadow: 0 8px 20px -6px color-mix(in srgb, var(--mc) 70%, transparent) !important;
        }
      `}</style>
      <button
        type="button"
        className="module-card"
        onClick={onOpen}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
          minHeight: 172, padding: 22, textAlign: 'center',
          background: 'var(--bg-card)', color: 'var(--text-primary)',
          border: '2px solid #94a3b8', borderRadius: 16,
          boxShadow: '0 2px 4px rgba(15,23,42,0.08), 0 14px 32px -10px rgba(7,30,83,0.35)',
          cursor: 'pointer', fontFamily: 'var(--font)', '--mc': liftForDark(color),
          transition: 'transform 0.18s cubic-bezier(0.22,1,0.36,1), box-shadow 0.18s, border-color 0.18s',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.borderColor = '#071e53';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(15,23,42,0.10), 0 26px 48px -14px rgba(7,30,83,0.55)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'none';
          e.currentTarget.style.borderColor = '#94a3b8';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(15,23,42,0.08), 0 14px 32px -10px rgba(7,30,83,0.35)';
        }}
      >
        <span className="module-card-icon" style={{
          width: 52, height: 52, borderRadius: 14, flexShrink: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: `${color}1a`, border: `1.5px solid ${color}33`, color,
        }}>
          {icon || (card
            ? card.svg
            : NAV_ICONS[item.id] || <span style={{ fontSize: 22, lineHeight: 1 }}>{item.icon}</span>)}
        </span>
        <div style={{ flex: 1 }}>
          <div className="module-card-title" style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em', marginBottom: 5 }}>{item.label}</div>
          {badge && (
            <span style={{ display: 'inline-block', marginBottom: 6, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#e2e8f0', color: '#475569' }}>{badge}</span>
          )}
          {item.description && (
            <div className="module-card-desc" style={{ fontSize: 13, lineHeight: 1.45, color: 'var(--text-secondary)' }}>{item.description}</div>
          )}
        </div>
        <span
          className="module-card-btn"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 7,
            padding: '8px 22px',
            background: 'linear-gradient(135deg, #071e53 0%, #0e3b9e 100%)',
            color: '#ffffff',
            fontSize: 13,
            fontWeight: 700,
            borderRadius: 999,
            border: '1px solid rgba(255,255,255,0.18)',
            boxShadow: '0 4px 12px rgba(3, 21, 64, 0.45)',
            letterSpacing: '0.02em',
            marginTop: 4,
            transition: 'all 0.18s ease',
          }}
        >
          <span>Open</span>
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ transition: 'transform 0.18s ease' }}
            className="module-card-arrow"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </span>
      </button>
    </>
  );
}
